import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Navigate, NavLink, Outlet, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useAuthToken } from '@/hooks/useAuthToken'
import { usePublishMutation } from '@/hooks/usePublishMutation'
import { useGoLiveMutation } from '@/hooks/useGoLiveMutation'
import { getBundles, getDraftManifest } from '@/api/bundles'
import { GoLiveModal } from '@/components/GoLiveModal'
import { InfoTooltip } from '@/components/InfoTooltip'
import { PublishModal } from '@/components/PublishModal'
import { StatusPill } from '@/components/StatusPill'
import { ThemeToggleButton } from '@/components/ThemeToggleButton'
import { Tooltip } from '@/components/Tooltip'
import { NAV_SECTIONS } from './navSections'

const PUBLISH_SUCCESS_REDIRECT_MS = 3000
const GO_LIVE_SUCCESS_CLOSE_MS = 3000

export function AppShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  // Shared across every screen (not just the Dashboard) - PRD 02 already
  // assumes this topbar shows a live draft indicator fed by real data.
  const bundlesQuery = useQuery({ queryKey: ['bundles'], queryFn: getBundles })
  const draftManifestQuery = useQuery({ queryKey: ['draft-manifest'], queryFn: getDraftManifest })

  const liveVersion = bundlesQuery.data?.find((bundle) => bundle.isLive)?.version
  const hasDraftChanges = draftManifestQuery.data?.some((entry) => entry.exists) ?? false

  // Frontend PRD 11 - button enablement mirrors backend PRD 26's own guard
  // 1:1: enabled only when the newest published bundle isn't already live.
  // `['bundles']` is newest-first (src/api/bundles.ts), so `[0]` is it.
  const newestBundle = bundlesQuery.data?.[0]
  const canGoLive = newestBundle ? !newestBundle.isLive : false

  const [publishModalOpen, setPublishModalOpen] = useState(false)
  const [releaseNote, setReleaseNote] = useState('')
  const publishMutation = usePublishMutation()

  const [goLiveModalOpen, setGoLiveModalOpen] = useState(false)
  const goLiveMutation = useGoLiveMutation()

  useEffect(() => {
    if (!publishMutation.isSuccess) return
    const timeout = setTimeout(() => navigate('/'), PUBLISH_SUCCESS_REDIRECT_MS)
    return () => clearTimeout(timeout)
  }, [publishMutation.isSuccess, navigate])

  // Unlike Publish's auto-navigate, Go Live has no obvious destination
  // screen (it's reachable from any screen via the topbar) - it just closes
  // itself instead, per the PRD's own States section.
  useEffect(() => {
    if (!goLiveMutation.isSuccess) return
    const timeout = setTimeout(() => setGoLiveModalOpen(false), GO_LIVE_SUCCESS_CLOSE_MS)
    return () => clearTimeout(timeout)
  }, [goLiveMutation.isSuccess])

  function openGoLiveModal() {
    // Clears any stale success/error state from a previous run before the
    // modal reopens - same role reset() plays in Publish's own onCancel.
    goLiveMutation.reset()
    setGoLiveModalOpen(true)
  }

  return (
    <div className="flex h-screen bg-bg text-text-primary">
      <aside className="flex w-[240px] shrink-0 flex-col border-r border-border bg-surface p-4">
        <div className="px-2 pb-4 text-title font-semibold">Legal Flow IDP</div>
        <nav className="flex flex-col gap-1">
          {NAV_SECTIONS.map(({ label, path, icon: Icon }) => (
            <NavLink
              key={path}
              to={path}
              end={path === '/'}
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-2 rounded-control px-3 py-2 text-body',
                  isActive
                    ? 'bg-surface-raised text-text-primary'
                    : 'text-text-secondary hover:text-text-primary',
                )
              }
            >
              <Icon size={16} strokeWidth={1.5} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto border-t border-border pt-4">
          <ThemeToggleButton />
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface px-6 text-body">
          <span className="flex items-center gap-1 text-text-secondary">
            Bundle: {bundlesQuery.isSuccess ? (liveVersion ?? '-') : '-'}
            <InfoTooltip text="The version of configuration currently controlling the live system." />
          </span>
          <div className="flex items-center gap-4">
            {draftManifestQuery.isSuccess && (
              <Tooltip
                text={
                  hasDraftChanges
                    ? 'You have edits not yet published.'
                    : 'No unpublished changes.'
                }
              >
                <StatusPill
                  status={hasDraftChanges ? 'draft' : 'live'}
                  label={hasDraftChanges ? 'Draft changes pending' : 'Live'}
                />
              </Tooltip>
            )}
            {hasDraftChanges ? (
              <Tooltip text="Creates a new version from your changes. This does not make it live, use Go Live for that.">
                <button
                  type="button"
                  onClick={() => setPublishModalOpen(true)}
                  className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white"
                >
                  Publish
                </button>
              </Tooltip>
            ) : (
              // aria-disabled (not the native `disabled` attribute) so the
              // Tooltip's hover/focus listeners keep firing - matches
              // Diff & Validation's own disabled-with-tooltip convention.
              <Tooltip text="No draft changes to publish">
                <button
                  type="button"
                  aria-disabled="true"
                  className="cursor-not-allowed rounded-control bg-accent/50 px-4 py-2 text-body font-medium text-white/70"
                >
                  Publish
                </button>
              </Tooltip>
            )}
            {canGoLive ? (
              <Tooltip text="Points the live system at this published version right away. This is a real, live change.">
                <button
                  type="button"
                  onClick={openGoLiveModal}
                  className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white"
                >
                  Go Live
                </button>
              </Tooltip>
            ) : (
              <Tooltip text="Already live">
                <button
                  type="button"
                  aria-disabled="true"
                  className="cursor-not-allowed rounded-control bg-accent/50 px-4 py-2 text-body font-medium text-white/70"
                >
                  Go Live
                </button>
              </Tooltip>
            )}
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1200px] flex-1 overflow-y-auto p-8">
          {children}
        </main>
      </div>

      <PublishModal
        open={publishModalOpen}
        liveVersion={liveVersion}
        releaseNote={releaseNote}
        onReleaseNoteChange={setReleaseNote}
        onConfirm={() => publishMutation.mutate(releaseNote)}
        onCancel={() => {
          setPublishModalOpen(false)
          publishMutation.reset()
          setReleaseNote('')
        }}
        onNavigateToDashboard={() => navigate('/')}
        isPending={publishMutation.isPending}
        isError={publishMutation.isError}
        error={publishMutation.error}
        isSuccess={publishMutation.isSuccess}
        result={publishMutation.data}
      />

      <GoLiveModal
        open={goLiveModalOpen}
        targetVersion={newestBundle?.version}
        onConfirm={() => goLiveMutation.mutate(undefined)}
        onCancel={() => {
          setGoLiveModalOpen(false)
          goLiveMutation.reset()
        }}
        isPending={goLiveMutation.isPending}
        isError={goLiveMutation.isError}
        error={goLiveMutation.error}
        isSuccess={goLiveMutation.isSuccess}
        result={goLiveMutation.data}
      />
    </div>
  )
}

// Route-level guard: every route other than /access renders through this.
// No token -> redirect before any screen's own data-fetching starts, rather
// than a client-side short-circuit inside apiRequest itself.
export function ProtectedShell() {
  const { token } = useAuthToken()

  if (!token) return <Navigate to="/access" replace />

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  )
}
